import { useState, useEffect } from 'react';
import useInfiniteList from '../hooks/useInfiniteList';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useSortableTable } from '../hooks/useSortableTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import {
  Plus,
  Pencil,
  Factory,
  Search,
  Phone,
  Mail,
  MapPin,
  User,
  MoreHorizontal,
  Truck,
  Users,
  Building2,
  Lock,
  Home,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import OperatorEditDrawer from '../components/OperatorEditDrawer';
import EquipmentEditDrawer from '../components/EquipmentEditDrawer';

const Suppliers = () => {
  const { api } = useAuth();
  const { currentTier, hasTierFeature } = useTier();
  const hasSubcontractorFeature = hasTierFeature('subcontractors') || currentTier === 'advanced' || currentTier === 'professional';
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [drawerKey, setDrawerKey] = useState(0);
  const [supplierEquipment, setSupplierEquipment] = useState([]);
  const [supplierOperators, setSupplierOperators] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [operatorEquipmentTypes, setOperatorEquipmentTypes] = useState([]);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [form, setForm] = useState({ company_name: '', contact_name: '', phone: '', email: '', address: '', notes: '', is_own_company: false });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState('active');
  const supList = useInfiniteList({
    endpoint: '/suppliers/paginated',
    search: searchTerm,
    params: { status: archiveFilter },
  });
  const suppliers = supList.items;

  const fetchSuppliers = () => { supList.reload(); };

  const fetchEquipmentTypes = async () => {
    try {
      const [typesRes, allTypesRes] = await Promise.all([api.get('/equipment-types'), api.get('/equipment-types/all')]);
      setEquipmentTypes(typesRes.data);
      setOperatorEquipmentTypes(allTypesRes.data.filter(et => et.is_active !== false));
    } catch (error) { console.error('Failed to fetch equipment types'); }
  };

  const fetchLinkedItems = async (supplierId) => {
    if (!supplierId) return;
    setLoadingLinked(true);
    try {
      const [equipmentRes, operatorsRes] = await Promise.all([api.get('/equipment'), api.get('/operators')]);
      setSupplierEquipment(equipmentRes.data.filter(e => e.supplier_id === supplierId));
      setSupplierOperators(operatorsRes.data.filter(o => o.supplier_id === supplierId));
    } catch (error) { console.error('Failed to fetch linked items'); }
    finally { setLoadingLinked(false); }
  };

  useEffect(() => { fetchEquipmentTypes(); }, [api, archiveFilter]);

  const resetForm = () => { setForm({ company_name: '', contact_name: '', phone: '', email: '', address: '', notes: '', is_own_company: false }); setEditingSupplier(null); setSupplierEquipment([]); setSupplierOperators([]); };

  const handleOpenDialog = (supplier = null, tab = 'details') => {
    if (supplier) { setEditingSupplier(supplier); setForm({ company_name: supplier.company_name, contact_name: supplier.contact_name, phone: supplier.phone || '', email: supplier.email || '', address: supplier.address || '', notes: supplier.notes || '', is_own_company: supplier.is_own_company || false }); fetchLinkedItems(supplier.id); }
    else { resetForm(); }
    setActiveTab(tab); setDrawerKey(k => k + 1); setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) { await api.put(`/suppliers/${editingSupplier.id}`, form); toast.success('Supplier updated'); }
      else { await api.post('/suppliers', form); toast.success('Supplier added'); }
      setDialogOpen(false); resetForm(); fetchSuppliers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleDelete = async (id) => {
    const supplier = suppliers.find(s => s.id === id);
    setSupplierToDelete(supplier);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!supplierToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/suppliers/${supplierToDelete.id}`);
      toast.success('Supplier archived');
      fetchSuppliers();
      setDeleteDialogOpen(false);
      setSupplierToDelete(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.reasons) {
        const r = detail.reasons;
        const parts = [];
        if (r.active_equipment) parts.push(`${r.active_equipment} active equipment`);
        if (r.active_workers) parts.push(`${r.active_workers} active workers`);
        toast.error(`Cannot archive supplier: ${parts.join(' and ')} assigned. Archive them first.`);
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Archive failed');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async (supplier) => {
    try {
      await api.post(`/suppliers/${supplier.id}/restore`);
      toast.success('Supplier restored');
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const handleOpenEquipmentDialog = (equipment = null) => { setEditingEquipment(equipment); setEquipmentDialogOpen(true); };
  const handleEquipmentSubmit = async (formData, equipment) => {
    try {
      if (equipment) { await api.put(`/equipment/${equipment.id}`, formData); toast.success('Equipment updated'); }
      else { await api.post('/equipment', formData); toast.success('Asset added'); }
      setEquipmentDialogOpen(false); setEditingEquipment(null);
      if (editingSupplier) fetchLinkedItems(editingSupplier.id);
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleOpenOperatorDialog = (operator = null) => { setEditingOperator(operator); setOperatorDialogOpen(true); };
  const handleOperatorSubmit = async (formData, operator) => {
    try {
      if (operator) { await api.put(`/operators/${operator.id}`, formData); toast.success('Worker updated'); }
      else { await api.post('/operators', formData); toast.success('Worker added'); }
      setOperatorDialogOpen(false); setEditingOperator(null);
      if (editingSupplier) fetchLinkedItems(editingSupplier.id);
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available': return <Badge className="status-available">Available</Badge>;
      case 'out_of_service': return <Badge className="status-out-of-service">Out of Service</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredSuppliers = suppliers.filter(s => s.company_name.toLowerCase().includes(searchTerm.toLowerCase()) || s.contact_name.toLowerCase().includes(searchTerm.toLowerCase()));

  const { sortedData: sortedSuppliers, requestSort, getSortDirection } = useSortableTable(filteredSuppliers, {
    company: (s) => (s.company_name || '').toLowerCase(),
    contact: (s) => (s.contact_name || '').toLowerCase(),
    phone: (s) => (s.phone || '').toLowerCase(),
    email: (s) => (s.email || '').toLowerCase(),
    address: (s) => (s.address || '').toLowerCase(),
  });

  // For Basic tier: only show the primary company supplier (first one, usually added at signup)
  const displayedSuppliers = hasSubcontractorFeature ? sortedSuppliers : sortedSuppliers.slice(0, 1);
  const isBasicTierLimited = !hasSubcontractorFeature && suppliers.length >= 1;

  if (supList.loading && suppliers.length === 0) return <div className="animate-pulse h-96 bg-muted rounded" />;

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div><h1 className="font-heading text-2xl md:text-3xl font-bold">Subcontractors</h1><p className="text-muted-foreground">Companies that provide equipment and operators</p></div>
        {hasSubcontractorFeature ? (
          <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-2" />Add Subcontractor</Button>
        ) : (
          <Button className="btn-volt opacity-50 cursor-not-allowed" disabled data-testid="add-supplier-btn-disabled">
            <Lock className="h-4 w-4 mr-2" />Add Subcontractor
          </Button>
        )}
      </div>

      {/* Basic Tier Upgrade Prompt */}
      {!hasSubcontractorFeature && (
        <UpgradePrompt 
          feature="Subcontractor Management"
          requiredTier="advanced"
          description="Manage multiple subcontractors, assign their equipment and operators to your jobs. Basic tier includes only your primary company."
        />
      )}

      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b"><SheetTitle className="font-heading text-xl flex items-center gap-2"><Building2 className="h-5 w-5 text-[#ffd108]" />{editingSupplier ? editingSupplier.company_name : 'Add New Subcontractor'}</SheetTitle></SheetHeader>
          {editingSupplier ? (
            <Tabs key={drawerKey} defaultValue={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details" className="flex items-center gap-2"><Pencil className="h-4 w-4" />Details</TabsTrigger>
                <TabsTrigger value="machines" className="flex items-center gap-2"><Truck className="h-4 w-4" />Assets ({supplierEquipment.length})</TabsTrigger>
                <TabsTrigger value="operators" className="flex items-center gap-2"><Users className="h-4 w-4" />Workers ({supplierOperators.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="mt-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Home className="h-5 w-5 text-blue-500" />
                      <div>
                        <Label className="font-medium">Own Company</Label>
                        <p className="text-sm text-muted-foreground">Mark this as your own business</p>
                      </div>
                    </div>
                    <Switch 
                      checked={form.is_own_company} 
                      onCheckedChange={(checked) => setForm({ ...form, is_own_company: checked })}
                      data-testid="own-company-toggle"
                    />
                  </div>
                  <div className="space-y-2"><Label className="label-overline">Company Name <span className="text-red-500">*</span></Label><Input data-testid="supplier-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="ABC Equipment Hire" required /></div>
                  <div className="space-y-2"><Label className="label-overline">Contact Name <span className="text-red-500">*</span></Label><Input data-testid="supplier-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="label-overline">Phone</Label><Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 234 567 890" /></div>
                    <div className="space-y-2"><Label className="label-overline">Email</Label><Input data-testid="supplier-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@supplier.com" /></div>
                  </div>
                  <div className="space-y-2"><Label className="label-overline">Address</Label><Textarea data-testid="supplier-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Industrial Ave, City" rows={2} /></div>
                  <div className="space-y-2"><Label className="label-overline">Notes</Label><Textarea data-testid="supplier-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." rows={2} /></div>
                  <div className="flex gap-2 pt-4 border-t mt-6"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" className="btn-volt" data-testid="supplier-submit-btn">Update Subcontractor</Button></div>
                </form>
              </TabsContent>
              <TabsContent value="machines" className="mt-4">
                <div className="flex justify-between items-center mb-4"><p className="text-sm text-muted-foreground">Assets owned by this supplier</p><Button size="sm" className="btn-volt" onClick={() => handleOpenEquipmentDialog()}><Plus className="h-4 w-4 mr-2" />Add Asset</Button></div>
                {loadingLinked ? <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd108]" /></div>
                : supplierEquipment.length === 0 ? <div className="text-center py-12 text-muted-foreground"><Truck className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No assets linked to this supplier</p><Button className="mt-4 btn-volt" onClick={() => handleOpenEquipmentDialog()}><Plus className="h-4 w-4 mr-2" />Add First Asset</Button></div>
                : <div className="space-y-2">{supplierEquipment.map((equipment) => (<div key={equipment.id} onClick={() => handleOpenEquipmentDialog(equipment)} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"><div className="flex items-center gap-3"><div className="p-2 rounded-full bg-blue-500/10"><Truck className="h-5 w-5 text-blue-500" /></div><div><p className="font-medium">{equipment.name}</p><div className="flex items-center gap-2 text-sm text-muted-foreground">{equipment.asset_id && <span className="font-mono">#{equipment.asset_id}</span>}{equipment.equipment_type && <Badge variant="secondary" className="text-xs">{equipment.equipment_type}</Badge>}{equipment.capacity && <span>{equipment.capacity}</span>}</div></div></div><div className="flex items-center gap-2">{getStatusBadge(equipment.status)}<Pencil className="h-4 w-4 text-muted-foreground" /></div></div>))}</div>}
                <SheetFooter className="pt-4 border-t mt-6"><Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button></SheetFooter>
              </TabsContent>
              <TabsContent value="operators" className="mt-4">
                <div className="flex justify-between items-center mb-4"><p className="text-sm text-muted-foreground">Workers employed by this supplier</p><Button size="sm" className="btn-volt" onClick={() => handleOpenOperatorDialog()}><Plus className="h-4 w-4 mr-2" />Add Worker</Button></div>
                {loadingLinked ? <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd108]" /></div>
                : supplierOperators.length === 0 ? <div className="text-center py-12 text-muted-foreground"><Users className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No workers linked to this supplier</p><Button className="mt-4 btn-volt" onClick={() => handleOpenOperatorDialog()}><Plus className="h-4 w-4 mr-2" />Add First Worker</Button></div>
                : <div className="space-y-2">{supplierOperators.map((operator) => (<div key={operator.id} onClick={() => handleOpenOperatorDialog(operator)} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"><div className="flex items-center gap-3"><div className="p-2 rounded-full bg-green-500/10"><User className="h-5 w-5 text-green-500" /></div><div><p className="font-medium">{operator.name}</p><div className="flex items-center gap-2 text-sm text-muted-foreground">{operator.operator_code && <Badge variant="outline" className="text-xs">{operator.operator_code}</Badge>}{operator.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{operator.phone}</span>}{operator.mobile_enabled && <Badge className="text-xs bg-green-500/20 text-green-600">Mobile</Badge>}</div></div></div><div className="flex items-center gap-2"><Badge variant={operator.is_available ? 'default' : 'secondary'} className="text-xs">{operator.is_available ? 'Available' : 'Unavailable'}</Badge><Pencil className="h-4 w-4 text-muted-foreground" /></div></div>))}</div>}
                <SheetFooter className="pt-4 border-t mt-6"><Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button></SheetFooter>
              </TabsContent>
            </Tabs>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Home className="h-5 w-5 text-blue-500" />
                  <div>
                    <Label className="font-medium">Own Company</Label>
                    <p className="text-sm text-muted-foreground">Mark this as your own business</p>
                  </div>
                </div>
                <Switch 
                  checked={form.is_own_company} 
                  onCheckedChange={(checked) => setForm({ ...form, is_own_company: checked })}
                  data-testid="own-company-toggle-new"
                />
              </div>
              <div className="space-y-2"><Label className="label-overline">Company Name <span className="text-red-500">*</span></Label><Input data-testid="supplier-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="ABC Equipment Hire" required /></div>
              <div className="space-y-2"><Label className="label-overline">Contact Name <span className="text-red-500">*</span></Label><Input data-testid="supplier-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" required /></div>
              <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label className="label-overline">Phone</Label><Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 234 567 890" /></div><div className="space-y-2"><Label className="label-overline">Email</Label><Input data-testid="supplier-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@supplier.com" /></div></div>
              <div className="space-y-2"><Label className="label-overline">Address</Label><Textarea data-testid="supplier-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Industrial Ave, City" rows={2} /></div>
              <div className="space-y-2"><Label className="label-overline">Notes</Label><Textarea data-testid="supplier-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." rows={2} /></div>
              <SheetFooter className="pt-4 border-t mt-6"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" className="btn-volt" data-testid="supplier-submit-btn">Add Subcontractor</Button></SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <EquipmentEditDrawer open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen} equipment={editingEquipment} suppliers={suppliers} equipmentTypes={equipmentTypes} onSubmit={handleEquipmentSubmit} defaultSupplierId={editingSupplier?.id} />
      <OperatorEditDrawer open={operatorDialogOpen} onOpenChange={setOperatorDialogOpen} operator={editingOperator} suppliers={suppliers} equipmentTypes={operatorEquipmentTypes} onSubmit={handleOperatorSubmit} defaultSupplierId={editingSupplier?.id} />

      <Card className="card-industrial">
        <CardHeader className="pb-3"><div className="flex items-center gap-4"><div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input data-testid="supplier-search" placeholder="Search subcontractors..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" /></div><Select value={archiveFilter} onValueChange={setArchiveFilter}><SelectTrigger className="w-[140px]" data-testid="supplier-archive-filter"><SelectValue placeholder="Active" /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select></div></CardHeader>
        <CardContent>
          {displayedSuppliers.length === 0 ? <div className="text-center py-12"><Factory className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">No subcontractors found</p>{hasSubcontractorFeature && <Button className="mt-4 btn-volt" onClick={() => handleOpenDialog()}>Add Your First Subcontractor</Button>}</div>
          : <div className="overflow-x-auto"><Table><TableHeader><TableRow><SortableTableHead sortKey="company" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Company</SortableTableHead><SortableTableHead sortKey="contact" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Contact</SortableTableHead><SortableTableHead sortKey="phone" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Phone</SortableTableHead><SortableTableHead sortKey="email" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Email</SortableTableHead><SortableTableHead sortKey="address" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Address</SortableTableHead><TableHead className="label-overline text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{displayedSuppliers.map((supplier) => (<TableRow key={supplier.id} data-testid={`supplier-row-${supplier.id}`} className="cursor-pointer hover:bg-muted/50" onClick={(e) => { if (e.target.closest('button')) return; handleOpenDialog(supplier); }}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {supplier.company_name}
                  {supplier.archived && (
                    <Badge variant="outline" className="text-xs text-muted-foreground border-muted-foreground/40 bg-muted/30" data-testid={`archived-badge-${supplier.id}`}>Archived</Badge>
                  )}
                  {supplier.is_own_company && (
                    <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30">
                      <Home className="h-3 w-3 mr-1" />
                      Own Company
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell><span className="flex items-center gap-1"><User className="h-3 w-3 text-muted-foreground" />{supplier.contact_name}</span></TableCell>
              <TableCell>{supplier.phone ? <span className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" />{supplier.phone}</span> : '-'}</TableCell>
              <TableCell>{supplier.email ? <span className="flex items-center gap-1 text-muted-foreground"><Mail className="h-3 w-3" />{supplier.email}</span> : '-'}</TableCell>
              <TableCell>{supplier.address ? <span className="flex items-center gap-1 text-muted-foreground text-sm max-w-[200px] truncate"><MapPin className="h-3 w-3 flex-shrink-0" />{supplier.address}</span> : '-'}</TableCell>
              <TableCell className="text-right"><DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`supplier-actions-${supplier.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDialog(supplier, 'details'); }} data-testid={`edit-supplier-${supplier.id}`}><Pencil className="h-4 w-4 mr-2" />Edit Details</DropdownMenuItem><DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDialog(supplier, 'machines'); }}><Truck className="h-4 w-4 mr-2" />Assets</DropdownMenuItem><DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDialog(supplier, 'operators'); }}><Users className="h-4 w-4 mr-2" />Workers</DropdownMenuItem>{hasSubcontractorFeature && <><DropdownMenuSeparator />{supplier.archived ? (<DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRestore(supplier); }} data-testid={`restore-supplier-${supplier.id}`}><ArchiveRestore className="h-4 w-4 mr-2" />Restore</DropdownMenuItem>) : (<DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(supplier.id); }} className="text-destructive focus:text-destructive" data-testid={`archive-supplier-${supplier.id}`}><Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>)}</>}</DropdownMenuContent></DropdownMenu></TableCell>
            </TableRow>))}</TableBody></Table></div>}
          <div ref={supList.sentinelRef} />
          {displayedSuppliers.length > 0 && (
            <div className="flex items-center justify-between pt-4 text-sm text-muted-foreground" data-testid="suppliers-pagination-footer">
              <span>Showing {displayedSuppliers.length} of {supList.total}</span>
              {supList.hasMore && (
                <Button size="sm" variant="outline" onClick={supList.loadMore} data-testid="suppliers-load-more">Load more</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Archive Supplier"
        description={`Archive "${supplierToDelete?.company_name}"? Archived suppliers are hidden from pickers and new assignments but historical records are preserved. You can restore them later from the Archived filter.`}
        confirmText="Archive"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="archive-supplier-dialog"
      />
    </div>
  );
};

export default Suppliers;
